import { addDays, addYears, differenceInDays } from 'date-fns'
import React, { CSSProperties } from 'react'
import { Slider, withStyles } from '@material-ui/core'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { FiSettings } from 'react-icons/fi'
import { theme } from '../../../theme'
import { IStore } from '../../../reducers'
import { ChosenCurrencyValue } from '../../Form/PriceConfiguration/PriceStyle'
import CalendarPicker from './CalendarPicker'
import { FilterBarContainer } from './FilterBar.styled'
import Flex from './Flex'
import Printer from './Printer'
import RangePicker from './RangePicker'
import TodayPicker from './TodayPicker'
import YearPicker from './YearPicker'
import { MonthPicker } from './MonthPicker'
import Button from './Printer/Button'
import { getAirbnbPriceTab } from '../../../reducers/airbnb/selectors'
import useIsScope from '../../../hooks/useIsScope'
import { PermissionScope } from '../../Employees'
import { fireToastError } from '../../../actions/toasts/fireToast'
import { Mixpanel } from '../../../utils/Mixpanel'
import { getWorkerId } from '../../../reducers/userAccount/selectors/userAccount'

export const BBSlider = withStyles({
  root: {
    color: theme.colors.palette.emerald,
  },
})(Slider)

function FilterBar({
  startDate,
  finishDate,
  changeDate,
  isChanging,
  change,
  size = 1,
  print = false,
  currency = undefined,
  wrapperStyle,
  minDate,
  maxDate,
}: IFilterBarProps & IFilterBarDispatch) {
  const { t } = useTranslation()
  const priceTab = useSelector(getAirbnbPriceTab)
  const dispatch = useDispatch()
  const workerId = useSelector(getWorkerId)

  const showSlider = useSelector(
    (state: IStore) => state.bookingCalendar.size.showSliders,
  )

  function modifyYear(plus: number) {
    return function() {
      changeDate(addYears(startDate, plus), addYears(finishDate, plus))
    }
  }

  function modifyWeek(plus: number) {
    return function() {
      const diff = Math.abs(differenceInDays(startDate, finishDate))
      const result = plus * diff

      changeDate(addDays(startDate, result), addDays(finishDate, result))
    }
  }

  function toToday() {
    return function() {
      const diff = Math.abs(differenceInDays(startDate, finishDate))

      changeDate(new Date(), addDays(new Date(), diff))
    }
  }

  function toDay() {
    return function(date: Date) {
      const diff = Math.abs(differenceInDays(startDate, finishDate))
      const lastDate = addDays(date, diff)
      changeDate(date, lastDate)
    }
  }

  function changeSize(event, value) {
    if (change) {
      change(Number(value) / 100)
    }
  }

  function value(val) {
    return `${val}%`
  }

  const scopes = useIsScope({
    config: [PermissionScope.SCOPE_CAN_USE_PRICING_SETTINGS],
  })

  function goToConfig() {
    if (!scopes.config) {
      Mixpanel.track('Employee Access Denied', {
        'Employee ID': workerId,
      })
      dispatch(fireToastError(t('BED_3621_no_permission')))
    } else {
      if ('routerHistory' in window) {
        ;(window as any).routerHistory.push(
          priceTab ? '/integration/airbnb' : '/price/configuration',
          {},
        )
      }
    }
  }

  return (
    <FilterBarContainer
      justifyStart={true}
      currency={!!currency}
      alignCenter={true}
      style={{ minHeight: `${40 * size}px`, width: '100%', ...wrapperStyle }}
    >
      <Flex justifyStart={true} alignCenter={true} full={true}>
        <RangePicker
          size={size}
          startDate={startDate}
          finishDate={finishDate}
          modifyWeek={modifyWeek}
          className={'calendar-range_picker'}
          isChanging={isChanging}
          print={print}
        />
        <YearPicker
          size={size}
          finishDate={finishDate}
          modifyWeek={modifyYear}
          className={'calendar-year_picker'}
          isChanging={isChanging}
          print={print}
        />
        {!print && (
          <>
            <CalendarPicker
              size={size}
              startDate={startDate}
              changeDate={toDay}
              minDate={minDate}
              maxDate={maxDate}
              className={'calendar-calendar_picker'}
            />
            <TodayPicker
              size={size}
              changeDate={toToday}
              className={'calendar-today_picker'}
            />
            <MonthPicker
              toDay={toDay}
              size={size}
              startDate={startDate}
              minDate={minDate}
              maxDate={maxDate}
            />
          </>
        )}
      </Flex>
      {!print && (
        <>
          {showSlider && (
            <div
              style={{
                position: 'fixed',
                right: '90px',
                width: '320px',
              }}
            >
              <BBSlider
                defaultValue={size * 100}
                valueLabelFormat={value}
                valueLabelDisplay="auto"
                step={5}
                onChangeCommitted={changeSize}
                marks={true}
                min={50}
                max={120}
              />
            </div>
          )}
          {!print && !currency && (
            <Flex>
              <Printer className={'false'} size={size} />
            </Flex>
          )}
          {currency && (
            <Flex>
              <Flex
                alignCenter={true}
                style={{
                  color: '#696a6d',
                  fontSize: `14px`,
                  fontWeight: 500,
                  width: '100%',
                }}
              >
                {t('profile_currency')}:&nbsp;
                <ChosenCurrencyValue>{currency}</ChosenCurrencyValue>
                <Button
                  size={size}
                  outline={true}
                  onClick={goToConfig}
                  style={{ marginLeft: '10px' }}
                >
                  <FiSettings size={16 * size} />
                </Button>
              </Flex>
            </Flex>
          )}
        </>
      )}
    </FilterBarContainer>
  )
}

export default FilterBar

export interface IFilterBarProps {
  startDate?: Date
  finishDate?: Date
  isChanging: boolean
  size?: number
  print?: boolean
  wrapperStyle?: CSSProperties
  currency?: string
  minDate?: Date
  maxDate?: Date
}

export interface IFilterBarDispatch {
  changeDate: (startDate: Date, finishDate: Date) => void
  change?: (size) => void
}
